import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, FormLabel, IconButton, Link, Radio, RadioGroup, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, {useEffect, useRef, useState} from 'react';
import {shadow} from '../assets/colors';
import {DarkButton} from './buttons';
import ErrorMessage from './error';
import RenewalStore from '../stores/renewal-store';
import SignatureCanvas from 'react-signature-canvas';
import {observeSignatureCanvas, signatureCanvasValue} from '../assets/signature-canvas';
import {FilingSignature, officerName, renewalConsent, saveRenewalSignature, signatureIsValid, signatureMatchesName} from '../assets/renewal-signature';
import {observer} from 'mobx-react';
import {registeredAgentName} from '../assets/company-review';
import {approveRegisteredAgent, completeReinstatementSignatures, RegisteredAgentApproval,
    reinstatementAgentConsent, reinstatementConsent, ReinstatementSignatures} from '../assets/reinstatement-signature';

export interface SignatureDialogProps {
    open: boolean;
    onClose: () => void;
    onSigned?: () => void;
    officers?: any[];
    documentNumber: string;
    singleSigner?: {
        heading: string;
        companyName: string;
        name: string;
        reviewVersion: string;
        consent: typeof renewalConsent;
        onSign: (signature: FilingSignature) => Promise<void>;
    };
    reinstatement?: {
        onSign: (signatures: ReinstatementSignatures, reviewedCompany: any) => Promise<void>;
        onUpdateAgent: () => void;
        registeredAgentLocked: boolean;
    };
}

export const SignatureDialog = observer(({onClose, onSigned, open, officers = [], documentNumber, reinstatement, singleSigner}: SignatureDialogProps) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [useTypedSignature, setUseTypedSignature] = useState(false);
    const [selectedOfficerId, setSelectedOfficerId] = useState('');
    const [officerSelectionOpen, setOfficerSelectionOpen] = useState(!reinstatement && !singleSigner);
    const [agentApproval, setAgentApproval] = useState<RegisteredAgentApproval | null>(null);
    const [signatureString, setSignatureString] = useState('');
    const [principalName, setPrincipalName] = useState('');
    const [sigPad, setSigPad] = useState<SignatureCanvas | null>(null);
    const inFlight = useRef(false);
    const dialogContent = useRef<HTMLDivElement>(null);
    const signatureHeading = useRef<HTMLHeadingElement>(null);
    const officerChoices = useRef<HTMLDivElement>(null);
    const officer = selectedOfficerId === '' ? null : officers[Number(selectedOfficerId)];
    const signingAgent = !!reinstatement && !agentApproval;
    const agent = RenewalStore.getUpdatedCompanyInfo?.registeredAgent;
    const agentIsEntity = agent?.type === 'C';
    const agentTypeKnown = ['P', 'C'].includes(agent?.type);
    const signerName = singleSigner ? singleSigner.name.trim() : signingAgent ? (agentIsEntity ? principalName.trim() : registeredAgentName(agent)) : officer ? officerName(officer) : '';
    const canSign = singleSigner ? !!signerName : signingAgent ? agentTypeKnown && !reinstatement?.registeredAgentLocked && !!signerName : !!officer;
    const validSignature = singleSigner ? signatureMatchesName(signerName, signatureString, useTypedSignature) : signingAgent
        ? canSign && signatureMatchesName(signerName, signatureString, useTypedSignature)
            && (!agentIsEntity || signerName.toLowerCase() !== registeredAgentName(agent).toLowerCase())
        : signatureIsValid(officer, signatureString, useTypedSignature);
    const consent = singleSigner?.consent ?? (signingAgent ? reinstatementAgentConsent : reinstatement ? reinstatementConsent : renewalConsent);
    const reviewVersion = singleSigner?.reviewVersion ?? (reinstatement ? JSON.stringify(RenewalStore.getUpdatedCompanyInfo) : '');

    const clear = () => {
        if (sigPad?.getCanvas()?.isConnected) sigPad.clear();
        setSignatureString('');
        setErrorMessage('');
    };

    useEffect(() => {
        if (open) {
            setSelectedOfficerId('');
            setAgentApproval(null);
            setOfficerSelectionOpen(!reinstatement && !singleSigner);
            setPrincipalName('');
            setUseTypedSignature(false);
            clear();
        }
    }, [open, documentNumber, reviewVersion]);

    useEffect(() => {
        if (!open) return;
        if (officerSelectionOpen) {
            officerChoices.current?.querySelector<HTMLInputElement>('input:checked, input')?.focus();
        } else if (canSign) {
            if (dialogContent.current) dialogContent.current.scrollTop = 0;
            signatureHeading.current?.focus({preventScroll: true});
        }
    }, [open, officerSelectionOpen, selectedOfficerId, signingAgent]);

    useEffect(() => {
        if (!open || useTypedSignature || !sigPad) return;
        return observeSignatureCanvas(sigPad, setSignatureString);
    }, [open, useTypedSignature, sigPad]);

    const handleClose = () => {
        if (inFlight.current) return;
        clear();
        onClose();
    };

    const signDocument = async () => {
        if (inFlight.current || !validSignature || officerSelectionOpen) return;
        if (signingAgent) {
            try {
                setAgentApproval(approveRegisteredAgent(RenewalStore.getUpdatedCompanyInfo, principalName, signatureString, useTypedSignature));
                setSelectedOfficerId('');
                setOfficerSelectionOpen(true);
                setUseTypedSignature(false);
                clear();
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : 'Please review the registered-agent signature.');
            }
            return;
        }
        inFlight.current = true;
        setSaving(true);
        setErrorMessage('');
        const reviewedCompany = singleSigner ? null : JSON.parse(JSON.stringify(RenewalStore.getUpdatedCompanyInfo));
        try {
            if (singleSigner) {
                await singleSigner.onSign({name: signerName, signatureValue: signatureString.trim(),
                    signatureType: useTypedSignature ? 'typed' : 'drawn', accepted: true, consent});
            } else if (reviewedCompany.id !== documentNumber) {
                throw new Error('The company has changed. Please review and sign again.');
            } else if (reinstatement) {
                await reinstatement.onSign(completeReinstatementSignatures(agentApproval, reviewedCompany, Number(selectedOfficerId), signatureString, useTypedSignature), reviewedCompany);
            } else {
                const signature = await saveRenewalSignature(
                    JSON.parse(JSON.stringify(RenewalStore.getCompanyInfo)),
                    reviewedCompany, Number(selectedOfficerId), signatureString, useTypedSignature,
                );
                RenewalStore.recordSignature(signature, officer.id ?? null, reviewedCompany);
            }
            onClose();
            onSigned?.();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Your signature could not be saved. Please try again.');
        } finally {
            inFlight.current = false;
            setSaving(false);
        }
    };

    return (
        <Dialog fullWidth maxWidth="sm" slotProps={{backdrop: {style: {backgroundColor: shadow}}}}
                PaperProps={{sx: {m: {xs: 2, sm: 4}, width: {xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)'}, maxHeight: {xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)'}}}}
                onClose={handleClose} open={open} aria-labelledby="renewal-signature-title">
            <DialogTitle id="renewal-signature-title" sx={{pl: {xs: 2, sm: 3}, pr: 8, pb: 1, fontSize: {xs: '1.125rem', sm: '1.25rem'}, fontWeight: 700}}>
                {singleSigner?.heading ?? (reinstatement ? 'Authorize Reinstatement' : 'Authorize Renewal')}
                <IconButton type="button" aria-label="Close authorization" onClick={handleClose} disabled={saving}
                            sx={{position: 'absolute', right: 8, top: 4, width: 44, height: 44}}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent ref={dialogContent} sx={{overflowX: 'hidden', px: {xs: 2, sm: 3}, pb: 0}}>
                <Typography variant="body2" sx={{mb: {xs: 1.5, sm: 2}}}>{singleSigner?.companyName ?? RenewalStore.getUpdatedCompanyInfo?.name} ({documentNumber})</Typography>
                <Box component="fieldset" disabled={saving} sx={{border: 0, p: 0, m: 0, minWidth: 0, pointerEvents: saving ? 'none' : undefined}}>
                    {reinstatement && <Typography component="h3" sx={{fontSize: '1.25rem', fontWeight: 700, mb: 1.5}}>
                        {signingAgent ? 'Step 1. Registered Agent signature' : 'Step 2. Officer signature'}
                    </Typography>}
                    {reinstatement && agentApproval && <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1.5}}>
                        <Typography variant="body2" color="text.secondary">Registered agent signed: {agentApproval.signature.name}</Typography>
                        <Link component="button" type="button" disabled={saving} aria-label="Change registered-agent signature"
                              sx={{color: 'text.secondary', flexShrink: 0}} onClick={() => {
                                  setAgentApproval(null); setSelectedOfficerId(''); setOfficerSelectionOpen(false); setUseTypedSignature(false); clear();
                              }}>Change</Link>
                    </Box>}
                    {singleSigner ? <Box sx={{p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50'}}>
                        <Typography variant="caption" color="text.secondary">Authorized person</Typography>
                        <Typography sx={{fontWeight: 700}}>{signerName}</Typography>
                    </Box> : signingAgent && reinstatement ? <Box sx={{p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50'}}>
                        <Typography variant="caption" color="text.secondary">Registered agent</Typography>
                        <Typography sx={{fontWeight: 700}}>{registeredAgentName(agent) || 'Registered agent information required'}</Typography>
                        {reinstatement.registeredAgentLocked ? <Typography variant="body2" sx={{mt: 1}}>
                            This designation does not require an agent signature. <Link href="mailto:support@fcfiling.com">Contact support</Link> to arrange this reinstatement.
                        </Typography> : <>
                            <Link component="button" type="button" onClick={reinstatement.onUpdateAgent} disabled={saving}
                                  sx={{fontSize: '0.875rem', color: 'text.secondary', mt: 0.5}}>Update registered agent</Link>
                            {!agentTypeKnown && <Typography variant="body2">Select Individual or Business entity in the registered-agent details before signing.</Typography>}
                            {agentIsEntity && <TextField fullWidth label="Your legal name" value={principalName} sx={{mt: 2}}
                                                       helperText="A principal of the registered-agent business must sign in their own name."
                                                       onChange={event => {setPrincipalName(event.target.value); clear();}}/>}
                        </>}
                    </Box> : officerSelectionOpen ? <FormControl fullWidth>
                        <FormLabel id="renewal-signing-officer" sx={{fontSize: '1.25rem', fontWeight: 700, color: 'text.primary', '&.Mui-focused': {color: 'text.primary'}}}>
                            {reinstatement ? 'Select the signing officer' : '1. Select the signing officer'}
                        </FormLabel>
                        <Typography id="renewal-officer-instructions" sx={{mt: 0.5, mb: 1.5}}>Tap an officer below to open the signature box.</Typography>
                        {reinstatement && !officers.some(candidate => candidate.remove !== true) && <Typography variant="body2" color="error">
                            Add at least one officer or authorized representative in the company details before continuing.
                        </Typography>}
                        <RadioGroup ref={officerChoices} aria-labelledby="renewal-signing-officer" aria-describedby="renewal-officer-instructions" value={selectedOfficerId}
                                    sx={{gap: 1}} onChange={event => { setSelectedOfficerId(event.target.value); setOfficerSelectionOpen(false); clear(); }}>
                            {officers.map((candidate, index) => candidate.remove === true ? null :
                                <FormControlLabel key={index} value={String(index)} disabled={saving}
                                                  onClick={() => { if (selectedOfficerId === String(index)) setOfficerSelectionOpen(false); }}
                                                  control={<Radio sx={{p: 1, '& .MuiSvgIcon-root': {fontSize: 28}}}/>}
                                                  sx={{
                                                      m: 0, p: 1, minHeight: 72, boxSizing: 'border-box', border: '2px solid', borderRadius: 1,
                                                      borderColor: selectedOfficerId === String(index) ? 'primary.main' : 'grey.400',
                                                      bgcolor: selectedOfficerId === String(index) ? 'action.selected' : 'background.paper',
                                                      '& .MuiFormControlLabel-label': {flex: 1, minWidth: 0},
                                                      '&:hover': {borderColor: 'primary.main', bgcolor: 'action.hover'},
                                                      '&:focus-within': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2},
                                                  }}
                                                  label={<Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                                                      <Box sx={{minWidth: 0, overflowWrap: 'anywhere'}}>
                                                          <Typography sx={{fontSize: '1.125rem', fontWeight: 700}}>{officerName(candidate)}</Typography>
                                                          {candidate.title && <Typography variant="body2">Title: {candidate.title}</Typography>}
                                                      </Box>
                                                      {selectedOfficerId === String(index) && <Typography variant="body2" color="primary" sx={{fontWeight: 700}}>Selected</Typography>}
                                                  </Box>}/>
                            )}
                        </RadioGroup>
                    </FormControl> : officer && <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                        p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50'}}>
                        <Box sx={{minWidth: 0, overflowWrap: 'anywhere'}}>
                            <Typography variant="caption" color="text.secondary">Signing officer</Typography>
                            <Typography sx={{fontWeight: 700}}>
                                {officerName(officer)}{officer.title && <Typography component="span" variant="body2"> · {officer.title}</Typography>}
                            </Typography>
                        </Box>
                        <Button type="button" disabled={saving} aria-label="Change signing officer" onClick={() => setOfficerSelectionOpen(true)}
                                sx={{minHeight: 44, textTransform: 'none', flexShrink: 0}}>Change</Button>
                    </Box>}
                    <Box hidden={officerSelectionOpen}>
                        {canSign && <Typography component="h3" ref={signatureHeading} tabIndex={-1}
                                                sx={{fontSize: '1.25rem', fontWeight: 700, mt: 1.5, mb: 1}}>{reinstatement || singleSigner ? 'Sign below' : '2. Sign below'}</Typography>}
                        {canSign && (useTypedSignature ?
                            <TextField fullWidth className="signature-box" label="Sign Here" margin="normal"
                                       placeholder="Please type your name as it appears above" value={signatureString}
                                       onChange={event => setSignatureString(event.target.value)}
                                       error={!!signatureString && !validSignature}
                                       helperText={signatureString && !validSignature ? `Signature must match ${singleSigner ? 'the authorized person' : signingAgent ? 'the registered-agent signer' : 'the selected officer'} exactly.` : undefined}/>
                            : <>
                                <Box sx={{width: '100%', boxSizing: 'border-box', border: '1px solid lightgrey'}}>
                                    <SignatureCanvas key={signingAgent ? 'agent' : `officer-${selectedOfficerId}`} ref={setSigPad} clearOnResize={false}
                                                     onEnd={() => setSignatureString(signatureCanvasValue(sigPad))}
                                                     canvasProps={{'aria-label': 'Draw your signature', style: {width: '100%', height: 200, display: 'block', touchAction: 'none'}}}/>
                                </Box>
                            </>)}
                        {canSign && <Box sx={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1}}>
                            {!useTypedSignature && <Button type="button" onClick={clear} sx={{px: 0, minHeight: 36, textTransform: 'none'}}>Clear signature</Button>}
                            <Button type="button" onClick={() => { clear(); setUseTypedSignature(!useTypedSignature); }}
                                    sx={{px: 0, minHeight: 36, textTransform: 'none'}}>
                                {useTypedSignature ? 'Draw signature instead' : 'Type signature instead'}
                            </Button>
                        </Box>}
                    </Box>
                </Box>
                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2, mb: 0.75}}>
                    <Typography id="renewal-terms-heading" variant="body2" sx={{fontWeight: 700}}>Terms &amp; authorization</Typography>
                    <Typography variant="body2" color="text.secondary">Scroll to read</Typography>
                </Box>
                <Box role="region" aria-labelledby="renewal-terms-heading" tabIndex={0}
                     sx={{height: {xs: 112, sm: 128}, boxSizing: 'border-box', overflowY: 'auto', overscrollBehavior: 'contain', scrollbarGutter: 'stable',
                         border: '1px solid', borderColor: 'grey.400', borderRadius: 1, bgcolor: 'grey.50', p: 1.5,
                         '&:focus-visible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2},
                         '& p': {mb: 1.5}}}>
                    <Typography variant="body2">{consent.signatureNotice}</Typography>
                    <Typography variant="body2">{consent.agreementText}</Typography>
                    <Typography variant="body2">{consent.disclosureText}</Typography>
                    <Typography variant="body2" sx={{fontWeight: 700}}>Cancellation policy</Typography>
                    <Typography variant="body2">{consent.cancellationText}</Typography>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5, fontSize: '0.875rem'}}>
                        <Link href={consent.termsUrl} target="_blank" rel="noopener noreferrer">Terms and Conditions</Link>
                        <Link href={consent.cancellationUrl} target="_blank" rel="noopener noreferrer">Cancellation policy FAQ</Link>
                    </Box>
                    <Typography variant="body2">{consent.paymentNotice}</Typography>
                </Box>
            </DialogContent>
            <DialogActions disableSpacing sx={{p: {xs: 2, sm: 3}, pt: {xs: 1.5, sm: 2}, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1}}>
                <Typography variant="body2">{consent.acceptanceSummary}</Typography>
                {errorMessage && <ErrorMessage message={errorMessage}/>}
                {errorMessage && reinstatement && <Typography variant="body2" align="center">
                    To correct the company details, close this window; your changes to them are kept.
                </Typography>}
                <DarkButton onClick={signDocument} disabled={!validSignature || officerSelectionOpen || saving}
                            sx={{p: 1.5, width: '100%', '&.Mui-disabled': {bgcolor: 'grey.200', color: 'text.secondary'}}}>
                    {saving ? <><CircularProgress size={20} sx={{mr: 1}}/>{reinstatement ? 'Saving signatures...' : 'Saving signature...'}</> : consent.buttonText}
                </DarkButton>
            </DialogActions>
        </Dialog>
    );
});
